import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Loader, ArrowRight, Mail } from 'lucide-react';
import { API_URL } from '../styles/shared';
import MarketPulzeLogo from '../components/MarketPulzeLogo';

const VerifyEmailPage = ({ token, onLoginSuccess }) => {
  const [status, setStatus] = useState('verifying'); // verifying | success | error | expired
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/verify-email?token=${token}`)
      .then(res => {
        setStatus('success');
        if (res.data.token && res.data.user && onLoginSuccess) {
          setTimeout(() => onLoginSuccess(res.data.token, res.data.user), 2000);
        }
      })
      .catch(err => {
        const msg = err.response?.data?.message || '';
        setStatus(msg.toLowerCase().includes('expired') ? 'expired' : 'error');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Countdown redirect on success
  useEffect(() => {
    if (status !== 'success') return;
    if (countdown <= 0) { window.location.href = '/'; return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) { setResendError('Please enter your email address'); return; }
    setResendLoading(true);
    setResendError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: resendEmail });
      setResendSent(true);
    } catch (err) {
      setResendError(err.response?.data?.message || 'Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#050d1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: '"DM Sans", system-ui, sans-serif' }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '3rem' }}>
        <MarketPulzeLogo size={36} />
        <span style={{ fontSize: '1.25rem', fontWeight: 900, background: 'linear-gradient(135deg,#00ff88,#00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>MarketPulze</span>
      </div>

      <div style={{ maxWidth: '480px', width: '100%', background: 'linear-gradient(135deg,rgba(12,25,50,0.9),rgba(18,32,60,0.9))', backdropFilter: 'blur(20px)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '24px', padding: '3rem 2.5rem', textAlign: 'center' }}>

        {/* VERIFYING */}
        {status === 'verifying' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,212,255,0.1)', border: '2px solid rgba(0,212,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <Loader size={28} style={{ color: '#00d4ff', animation: 'spin 1s linear infinite' }} />
            </div>
            <h2 style={{ color: '#f8fafc', fontSize: '1.5rem', fontWeight: 900, marginBottom: '0.75rem' }}>Verifying your email</h2>
            <p style={{ color: '#64748b', lineHeight: 1.7 }}>Please wait while we confirm your email address...</p>
          </>
        )}

        {/* SUCCESS */}
        {status === 'success' && (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(0,255,136,0.1)', border: '2px solid rgba(0,255,136,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', animation: 'popIn 0.4s ease' }}>
              <CheckCircle size={36} style={{ color: '#00ff88' }} />
            </div>
            <h2 style={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem' }}>Email verified!</h2>
            <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '0.5rem' }}>Your MarketPulze account is now active.</p>
            <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '2rem' }}>You can now access all features including AI stock analysis.</p>

            {/* Countdown ring */}
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,255,136,0.08)', border: '2px solid rgba(0,255,136,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 900, color: '#00ff88' }}>{countdown}</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1.5rem' }}>Redirecting to login in {countdown}s...</p>

            <a href="/" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.9rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.95rem', textDecoration: 'none' }}>
              Go to MarketPulze <ArrowRight size={16} />
            </a>
          </>
        )}

        {/* EXPIRED */}
        {status === 'expired' && (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,170,0,0.1)', border: '2px solid rgba(255,170,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <Mail size={32} style={{ color: '#ffaa00' }} />
            </div>
            <h2 style={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem' }}>Link expired</h2>
            <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '2rem' }}>This verification link has expired. Verification links are valid for 24 hours. Enter your email below and we'll send you a new one.</p>
            {!resendSent ? (
              <form onSubmit={handleResend}>
                <input type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)} placeholder="Enter your email address"
                  style={{ width: '100%', padding: '0.85rem 1rem', background: 'rgba(10,20,45,0.8)', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#f8fafc', fontSize: '0.95rem', outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                {resendError && <p style={{ color: '#ff4444', fontSize: '0.8rem', marginBottom: '0.75rem', textAlign: 'left' }}>{resendError}</p>}
                <button type="submit" disabled={resendLoading} style={{ width: '100%', padding: '0.9rem', background: 'linear-gradient(135deg,#ffaa00,#ff8800)', border: 'none', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.9rem', cursor: resendLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {resendLoading ? '⏳ Sending...' : '📧 Send New Verification Email'}
                </button>
              </form>
            ) : (
              <div style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '10px', padding: '1.25rem' }}>
                <CheckCircle size={24} style={{ color: '#00ff88', marginBottom: '0.5rem' }} />
                <p style={{ color: '#00ff88', fontWeight: 700, marginBottom: '0.25rem' }}>Email sent!</p>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Check your inbox for a new verification link. Don't forget to check your spam folder.</p>
              </div>
            )}
          </>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,68,68,0.1)', border: '2px solid rgba(255,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <XCircle size={36} style={{ color: '#ff4444' }} />
            </div>
            <h2 style={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem' }}>Verification failed</h2>
            <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '2rem' }}>This verification link is invalid or has already been used. If you're having trouble, please contact support.</p>
            <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.9rem 2rem', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: '10px', color: '#00d4ff', fontWeight: 700, textDecoration: 'none' }}>
              Back to MarketPulze
            </a>
            <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#475569' }}>Need help? <a href="mailto:support@marketpulze.com" style={{ color: '#00d4ff' }}>support@marketpulze.com</a></p>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        input::placeholder { color: #334155; }
      `}</style>
    </div>
  );
};

export default VerifyEmailPage;
